import { WidgetSize } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import { MaximizeSmall, MinimiseSmall } from '@wix/wix-ui-icons-common';
import React, { FC, ReactElement, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { logToggleWidgetSizeClickedAction } from '../../../bi';
import { useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { selectWidgetMobileView, selectWidgetSize } from '../../../common/selectors/widget';
import { Page } from '../../../common/types';
import { experiments } from '../../../experiments';
import { widgetSDK } from '../../../sdk/widgetSDK';
import { renderModel } from '../../../util/renderModel';
import { headerActionsDataHooks } from './HeaderActions.dataHooks';
import { HeaderActionsIcon } from './HeaderActionsIcon';

const widgetMaximizationDisabled = experiments.is('specs.helpwidget.widgetMaximization', 'false');

const tooltipContent: Record<WidgetSize, TLocalesKeys> = {
  [WidgetSize.Default]: 'header_action_icon_maximize',
  [WidgetSize.Maximized]: 'header_action_icon_minimize',
};

const dataHooks: Record<WidgetSize, string> = {
  [WidgetSize.Default]: headerActionsDataHooks.maximizeWidgetSizeIcon(),
  [WidgetSize.Maximized]: headerActionsDataHooks.resetWidgetSizeIcon(),
};

const iconComponent: Record<WidgetSize, ReactElement> = {
  [WidgetSize.Default]: <MaximizeSmall />,
  [WidgetSize.Maximized]: <MinimiseSmall />,
};

const isMobile = renderModel.isMobile();

export const ToggleWidgetSize: FC = () => {
  const store = useStore();
  const { t } = useTranslation();
  const widgetSize = useSelector(selectWidgetSize);
  const widgetMobileView = useSelector(selectWidgetMobileView);
  const currentPage = useCurrentPage();

  // TODO: simplify logic (doing the same)
  const shouldShowButton = currentPage === Page.Article && !isMobile && !widgetMobileView;

  const onClick = useCallback(() => {
    const size = widgetSize === WidgetSize.Maximized ? WidgetSize.Default : WidgetSize.Maximized;

    logToggleWidgetSizeClickedAction(store.getState(), { currentPage });
    widgetSDK.setWidgetSize({ size });
  }, [widgetSize, currentPage, store]);

  if (widgetMaximizationDisabled || !shouldShowButton) {
    return null;
  }

  return (
    <HeaderActionsIcon
      onClick={onClick}
      dataHook={dataHooks[widgetSize]}
      tooltipContent={t(tooltipContent[widgetSize])}
    >
      {iconComponent[widgetSize]}
    </HeaderActionsIcon>
  );
};
